import moment, { Moment } from "moment";

import { Deployment, DeploymentDTO } from "./Deployment";
import { Import, ImportDTO } from "./Import";
import {
  ApplicationFeatureFlags,
  ApplicationFeatureFlagsDTO
} from "./ApplicationFeatureFlags";

export enum ApplicationState {
  NEW = "new",
  ARCHIVED = "archived",
  ERASED = "erased",
  DEPLOYED = "deployed"
}

export enum ApplicationType {
  NOTOUCH = "notouch",
  ENTERPRISE = "enterprise"
}

export interface ApplicationDTO {
  id: number;
  name: string;
  environment: string;
  type: ApplicationType;
  mainDomainName: string;
  createdAt: string;
  note: string;
  accountManagerNote: string;
  redirectDomainNames: string[];
  packages: string[];
  successfulDeploymentsCount: number;
  failedDeploymentsCount: number;
  latestDeployment: DeploymentDTO;
  latestImport: ImportDTO;
  state: ApplicationState;
  transitionState: ApplicationState;
  rewardCafeteriaEnabled: boolean;
  workflowsEnabled: boolean;
  oldAdminEnabled: boolean;
  billingEmail: string;
  billingForename: string;
  billingSurname: string;
  currency: string;
  hubSpotId: string;
  featureFlags?: ApplicationFeatureFlagsDTO;
}

export class Application {
  private _id: number;
  private _name: string;
  private _environment: string;
  private _type: ApplicationType;
  private _mainDomainName: string;
  private _createdAt: Moment;
  private _note: string;
  private _accountManagerNote: string;
  private _redirectDomainNames: string[];
  private _packages: string[];
  private _successfulDeploymentsCount: number;
  private _failedDeploymentsCount: number;
  private _latestDeployment?: Deployment;
  private _latestImport?: Import;
  private _state: ApplicationState;
  private _transitionState?: ApplicationState;
  private _rewardCafeteriaEnabled: boolean;
  private _workflowsEnabled: boolean;
  private _oldAdminEnabled: boolean;
  private _billingEmail: string;
  private _billingForename: string;
  private _billingSurname: string;
  private _currency: string;
  private _hubSpotId: string;
  private _featureFlags?: ApplicationFeatureFlags;

  static create(application: ApplicationDTO): Application {
    return new Application(application);
  }

  constructor(application: ApplicationDTO) {
    this._id = application.id;
    this._name = application.name;
    this._environment = application.environment || "live";
    this._type = application.type;
    this._mainDomainName = application.mainDomainName;
    this._createdAt = moment(application.createdAt);
    this._note = application.note;
    this._accountManagerNote = application.accountManagerNote;
    this._redirectDomainNames = application.redirectDomainNames;
    this._packages = application.packages;
    this._successfulDeploymentsCount = application.successfulDeploymentsCount;
    this._failedDeploymentsCount = application.failedDeploymentsCount;
    this._latestDeployment =
      application.latestDeployment &&
      Deployment.create(application.latestDeployment);
    this._latestImport =
      application.latestImport && Import.create(application.latestImport);
    this._state = application.state;
    this._transitionState = application.transitionState;
    this._rewardCafeteriaEnabled = application.rewardCafeteriaEnabled;
    this._workflowsEnabled = application.workflowsEnabled;
    this._oldAdminEnabled = application.oldAdminEnabled;
    this._billingEmail = application.billingEmail;
    this._billingForename = application.billingForename;
    this._billingSurname = application.billingSurname;
    this._currency = application.currency;
    this._hubSpotId = application.hubSpotId;
    this._featureFlags =
      application.featureFlags &&
      ApplicationFeatureFlags.create(application.featureFlags);
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get environment() {
    return this._environment;
  }

  public get type() {
    return this._type;
  }

  public get mainDomainName() {
    return this._mainDomainName;
  }

  public get note() {
    return this._note;
  }

  public get accountManagerNote() {
    return this._accountManagerNote;
  }

  public get redirectDomainNames() {
    return this._redirectDomainNames;
  }

  public get successfulDeploymentsCount() {
    return this._successfulDeploymentsCount;
  }

  public get packages() {
    return this._packages;
  }

  public get latestDeployment() {
    return this._latestDeployment;
  }

  public get latestImport() {
    return this._latestImport;
  }

  public get status() {
    if (!this._latestDeployment) {
      if (
        [ApplicationState.ARCHIVED, ApplicationState.ERASED].includes(
          this._state
        )
      ) {
        return this._state;
      } else if (this._state === ApplicationState.DEPLOYED) {
        return "successful";
      } else {
        return "offline";
      }
    }

    if (
      [
        this._latestDeployment.job.state === "in-progress",
        this._latestImport && this._latestImport.state === "in-progress"
      ].includes(true)
    ) {
      return "in-progress";
    }

    if (
      [
        this._latestDeployment.job.state === "failed",
        this._latestImport && this._latestImport.state === "failed"
      ].includes(true)
    ) {
      return "failed";
    }

    if (
      [ApplicationState.ARCHIVED, ApplicationState.ERASED].includes(this._state)
    ) {
      return this._state;
    }

    return this._latestDeployment.job.state;
  }

  public get createdAt() {
    return this._createdAt.format("DD.MM.YYYY  H:mm:ss");
  }

  public get state() {
    return this._state;
  }

  public get transitionState() {
    return this._transitionState;
  }

  public get rewardCafeteriaEnabled() {
    return this._rewardCafeteriaEnabled;
  }

  public get workflowsEnabled() {
    return this._workflowsEnabled;
  }

  public get oldAdminEnabled() {
    return this._oldAdminEnabled;
  }

  public set name(name) {
    this._name = name;
  }

  public set environment(environment) {
    this._environment = environment;
  }

  public set redirectDomainNames(redirectDomainNames) {
    this._redirectDomainNames = redirectDomainNames;
  }

  public set packages(packages) {
    this._packages = packages;
  }

  public set mainDomainName(mainDomainName) {
    this._mainDomainName = mainDomainName;
  }

  public set note(note) {
    this._note = note;
  }

  public set accountManagerNote(accountManagerNote) {
    this._accountManagerNote = accountManagerNote;
  }

  public set rewardCafeteriaEnabled(rewardCafeteriaEnabled) {
    this._rewardCafeteriaEnabled = rewardCafeteriaEnabled;
  }

  public set billingEmail(billingEmail) {
    this._billingEmail = billingEmail;
  }

  public get billingEmail() {
    return this._billingEmail;
  }

  public set billingForename(billingForename) {
    this._billingForename = billingForename;
  }

  public get billingForename() {
    return this._billingForename;
  }

  public set billingSurname(billingSurname) {
    this._billingSurname = billingSurname;
  }

  public get billingSurname() {
    return this._billingSurname;
  }

  public set currency(currency) {
    this._currency = currency;
  }

  public get currency() {
    return this._currency;
  }

  public set hubSpotId(hubSpotId) {
    this._hubSpotId = hubSpotId;
  }

  public get hubSpotId() {
    return this._hubSpotId;
  }

  public get featureFlags() {
    return this._featureFlags;
  }
}
